import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import userApi from '@/api/user/main';
import loginApi from '@/modules/auth/api/main';
import { getFirstLetter } from '@/common/utils/utilities';
import {
  AccountState,
  UserInfo,
  LoginPayload,
  GoogleLoginPayload,
  AuthResponse,
  ApiResponse,
  SetUserCheckedPayload,
  SetShowSignPayload,
} from '../interface';

// Async thunks
export const loginUser = createAsyncThunk(
  'account/login',
  async (payload: LoginPayload, { rejectWithValue }) => {
    try {
      const res: AuthResponse = await loginApi.signIn(payload);
      if (res.data.code === 200) {
        const token = `${res.data.data.token_type} ${res.data.data.access_token}`;
        window.localStorage.setItem('token', token);
        return res.data;
      } else {
        return rejectWithValue(res);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const loginWithGoogle = createAsyncThunk(
  'account/loginWithGoogle',
  async (payload: GoogleLoginPayload, { rejectWithValue }) => {
    try {
      const res: AuthResponse = await loginApi.handleGoogleCallback(payload);
      if (res.data.code === 200) {
        const token = `${res.data.data.token_type} ${res.data.data.access_token}`;
        window.localStorage.setItem('token', token);
        return res.data;
      } else {
        return rejectWithValue(res);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUserInfo = createAsyncThunk(
  'account/getUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const res: ApiResponse<UserInfo> = await userApi.UserInfo();
      if (res.code === 200) {
        return res.data;
      } else {
        return rejectWithValue(res);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Helper function to enrich user info
const enrichUserInfo = (userInfo: UserInfo): UserInfo => {
  const { first_name, last_name } = userInfo;
  return {
    ...userInfo,
    firstName: first_name,
    lastName: last_name,
    abbreviation: `${getFirstLetter(first_name || '')}${getFirstLetter(last_name || '')}`,
    diagnosisFormData: [],
    leagalFormData: [],
  };
};

// Initial state
const initialState: AccountState = {
  userInfo: {},
  showSign: false,
  loading: false,
  error: null,
};

// Slice
export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setUserInfo: (state, action: PayloadAction<UserInfo | undefined>) => {
      if (action.payload) {
        const enrichedUserInfo = enrichUserInfo(action.payload);
        window.localStorage.setItem('userInfo', JSON.stringify(enrichedUserInfo));
        state.userInfo = enrichedUserInfo;
      } else {
        // Clear user info
        window.localStorage.removeItem('userInfo');
        state.userInfo = {};
      }
      state.error = null;
    },

    setUserChecked: (state, action: PayloadAction<SetUserCheckedPayload>) => {
      const newUserInfo = {
        ...state.userInfo,
        is_checked: action.payload.is_checked,
      };
      window.localStorage.setItem('userInfo', JSON.stringify(newUserInfo));
      state.userInfo = newUserInfo;
    },

    setShowSign: (state, action: PayloadAction<SetShowSignPayload>) => {
      state.showSign = action.payload.showSign;
    },

    clearError: (state) => {
      state.error = null;
    },

    logout: (state) => {
      state.userInfo = {};
      state.showSign = false;
      state.error = null;
      window.localStorage.removeItem('userInfo');
      window.localStorage.removeItem('token');
    },
  },

  extraReducers: (builder) => {
    // Login user
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Login with Google
    builder
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get user info
    builder
      .addCase(getUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) {
          const enrichedUserInfo = enrichUserInfo(action.payload);
          window.localStorage.setItem('userInfo', JSON.stringify(enrichedUserInfo));
          state.userInfo = enrichedUserInfo;
        }
      })
      .addCase(getUserInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setUserInfo, setUserChecked, setShowSign, clearError, logout } = accountSlice.actions;

export default accountSlice.reducer;